import React, { useRef, useEffect, useState } from "react";
import { Box, InputLabel, FormControl, useTheme } from "@mui/material";
import LinkifyIt from "linkify-it";

const linkify = LinkifyIt();

const LinkDetectionTextField = ({
    value,
    onChange,
    label,
    labelWidth,
    shouldAutofocus = false,
    marginTop = 2,
    disabled = false
}) => {
    const theme = useTheme();
    const editorRef = useRef(null);
    const lastHtml = useRef("");
    const isComposing = useRef(false);
    const [isFocused, setIsFocused] = useState(false);
    const [cursorEndPositioned, setCursorEndPositioned] = useState(false);

    // Function to format text with links
    const formatContent = (text) => {
        if (!text) return "";

        // Detect all links in the text
        const links = linkify.match(text) || [];
        let lastIndex = 0;
        let result = "";

        // Process each link and replace it with a clickable anchor tag
        links.forEach((link) => {
            result += text.slice(lastIndex, link.index); // Text before the link
            result += `<a href="${
                link.url.startsWith("http") ? link.url : `https://${link.url}`
            }" target="_blank" rel="noopener noreferrer">${link.text}</a>`; // The link itself
            lastIndex = link.lastIndex;
        });

        result += text.slice(lastIndex); // Append remaining text after the last link

        // Replace newlines with <br> tags for line breaks
        result = result.replace(/\n/g, "<br>");
        return result;
    };

    const saveCursorPosition = () => {
        const selection = window.getSelection();
        if (!selection.rangeCount) return null;

        const range = selection.getRangeAt(0);
        let count = 0;
        const iterator = document.createNodeIterator(
            editorRef.current,
            NodeFilter.SHOW_ALL,
            {
                acceptNode: function (node) {
                    return NodeFilter.FILTER_ACCEPT;
                }
            }
        );

        let node;
        let found = false;
        const startContainer = range.startContainer;
        const startOffset = range.startOffset;

        while ((node = iterator.nextNode())) {
            if (node === startContainer) {
                if (node.nodeType === Node.TEXT_NODE) {
                    count += startOffset;
                } else {
                    count += startOffset;
                }
                found = true;
                break;
            }

            if (node.nodeType === Node.TEXT_NODE) {
                count += node.length;
            }
        }

        return {
            start: found ? count : 0,
            end: count + range.toString().length
        };
    };

    const restoreCursorPosition = (pos) => {
        if (!pos || !editorRef.current) return;

        const sel = window.getSelection();
        const range = document.createRange();
        let count = 0;
        let found = false;

        const walkNodes = (node) => {
            if (found) return;

            if (node.nodeType === Node.TEXT_NODE) {
                const length = node.length;
                if (count + length >= pos.start) {
                    const offset = pos.start - count;
                    range.setStart(node, Math.min(offset, length));
                    range.collapse(true);
                    found = true;
                }
                count += length;
            } else {
                Array.from(node.childNodes).forEach((child) => {
                    if (!found) walkNodes(child);
                });
            }
        };

        walkNodes(editorRef.current);

        // If the target position is not found, default to the last position
        if (!found) {
            range.selectNodeContents(editorRef.current);
            range.collapse(false); // Move to the end of the content
        }

        sel.removeAllRanges();
        sel.addRange(range);
    };

    const scrollToCursor = (editor) => {
        const selection = window.getSelection();
        if (!selection.rangeCount) return;

        const range = selection.getRangeAt(0);
        const rect = range.getBoundingClientRect(); // Get the position of the cursor
        const editorRect = editor.getBoundingClientRect(); // Get the position of the editor

        // Calculate the cursor's position relative to the editor
        const cursorTop = rect.top - editorRect.top + editor.scrollTop;

        // Ensure the editor scrolls to the cursor's position
        if (cursorTop < editor.scrollTop) {
            // Scroll up if the cursor is above the visible area
            editor.scrollTop = cursorTop;
        } else if (cursorTop > editor.scrollTop + editor.clientHeight) {
            // Scroll down if the cursor is below the visible area
            editor.scrollTop = cursorTop - editor.clientHeight + 18; // Add some padding
        }
    };

    const handleInput = () => {
        if (isComposing.current) return;

        const editor = editorRef.current;
        const cursorPos = saveCursorPosition();
        const text = editor.innerText; // Get the raw text content
        const textWithLinks = formatContent(text); // Format the text with links

        if (textWithLinks !== lastHtml.current) {
            // Use a temporary container to parse and compare the new HTML
            const tempDiv = document.createElement("div");
            tempDiv.innerHTML = textWithLinks;

            // Update only if the content has changed
            if (editor.innerHTML !== tempDiv.innerHTML) {
                editor.innerHTML = textWithLinks; // Update the content
                lastHtml.current = textWithLinks; // Save the last HTML state
                restoreCursorPosition(cursorPos); // Restore the cursor position
            }
        }

        scrollToCursor(editor);
        onChange(text);
    };

    // Open link in new tab
    const handleClickLink = (e) => {
        const link = e.target.closest("a");
        if (link) {
            e.preventDefault();
            window.open(link.href, "_blank", "noopener,noreferrer");
        }
    };

    const handleKeyDown = (e) => {
        const selection = window.getSelection();
        const range = selection.getRangeAt(0);

        if (e.key === "Enter") {
            e.preventDefault();

            const br = document.createElement("br");

            // Insert a <br> at the current cursor position
            range.deleteContents();
            range.insertNode(br);

            // Create an additional <br> if the line would otherwise collapse
            const doubleBreakNeeded =
                !br.nextSibling || br.nextSibling.nodeName !== "BR";
            if (doubleBreakNeeded) {
                const extraBr = document.createElement("br");
                range.insertNode(extraBr);
            }

            // Move the cursor after the <br>
            range.setStartAfter(br);
            range.setEndAfter(br);
            selection.removeAllRanges();
            selection.addRange(range);

            // Prevent re-rendering from interfering with the cursor
            lastHtml.current = editorRef.current.innerHTML;

            // Scroll to the bottom of the editor
            editorRef.current.scrollTop = editorRef.current.scrollHeight;
        }

        if (e.key === "Backspace") {
            const selection = window.getSelection();
            const range = selection.getRangeAt(0);

            const currNode = range.startContainer;

            // Check if the current node is a text node and has content
            if (currNode) {
                const prevBR = currNode.previousSibling; // previous <br>
                const prevExtraBR = prevBR?.previousSibling; // pevious <br> to the <br>

                if (
                    prevExtraBR &&
                    prevExtraBR?.nodeName === "BR" &&
                    currNode.nodeValue.length === 1
                ) {
                    const prevTextNode = prevExtraBR.previousSibling || null;
                    prevBR.remove();
                    prevExtraBR.remove();
                    range.setStart(
                        prevTextNode,
                        prevTextNode.textContent.length
                    );
                }

                return;
            }
            // If the text in current node is empty, check if the previous element is a <br> tag
            else if (currNode.previousElementSibling?.localName === "br") {
                e.preventDefault();

                const brToRemove = currNode.previousElementSibling;
                // Check if there is another <br> before the current <br>
                const brPrevNode = brToRemove.previousSibling;

                let prevTextNode;

                if (brPrevNode?.nodeName === "BR") {
                    prevTextNode = brPrevNode.previousSibling;
                    brToRemove.remove();
                    brPrevNode.remove();
                } else {
                    prevTextNode = brToRemove.previousSibling;
                    brToRemove.remove();
                }

                // Ensure the caret is placed at the end of the previous text node
                if (prevTextNode && prevTextNode.nodeType === Node.TEXT_NODE) {
                    range.setStart(
                        prevTextNode,
                        prevTextNode.textContent.length
                    );
                }

                range.collapse(true); // Collapse the range to a caret position
                selection.removeAllRanges();
                selection.addRange(range);
            } else {
                return;
            }
        }
    };

    const handleFocus = () => setIsFocused(true);
    const handleBlur = () => setIsFocused(false);

    useEffect(() => {
        if (editorRef.current) {
            if (value !== editorRef.current.innerText) {
                const formattedText = formatContent(value || "");
                editorRef.current.innerHTML = formattedText;
                lastHtml.current = formattedText;
            }

            if (shouldAutofocus && !cursorEndPositioned) {
                // Move the cursor to the end of the content
                const selection = window.getSelection();
                const range = document.createRange();
                range.selectNodeContents(editorRef.current);
                range.collapse(false); // Collapse the range to the end
                selection.removeAllRanges();
                selection.addRange(range);
                setCursorEndPositioned(true);

                editorRef.current.focus();
            }
        }
    }, [value]);

    return (
        <FormControl
            variant="outlined"
            fullWidth
            sx={{
                position: "relative",
                mt: marginTop,
                "& fieldset": {
                    "& legend": {
                        width: labelWidth
                    }
                }
            }}
        >
            {/* Hidden fieldset to mimic MUI border */}
            {(isFocused || value) && (
                <fieldset
                    aria-hidden
                    style={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        bottom: 0,
                        top: "-18px",
                        padding: "0 8px",
                        pointerEvents: "none",
                        border: isFocused
                            ? `2px solid ${theme.palette.primary.main}`
                            : `1px solid ${theme.palette.inputBorder.main}`,
                        borderRadius: "4px"
                    }}
                >
                    <legend style={{ padding: 0, textAlign: "left" }}>
                        <span
                            style={{
                                display: "inline-block",
                                opacity: isFocused || value ? 1 : 0
                            }}
                        >
                            {" "}
                        </span>
                    </legend>
                </fieldset>
            )}

            <InputLabel
                shrink={isFocused || value}
                sx={{
                    backgroundColor: "white !important",
                    position: "absolute",
                    fontSize: "14px",
                    transition: theme.transitions.create(
                        ["transform", "color"],
                        {
                            duration: theme.transitions.duration.shorter
                        }
                    ),
                    color:
                        isFocused || value
                            ? theme.palette.primary.main
                            : theme.palette.grey.light,
                    pointerEvents: "none"
                }}
            >
                {label}
            </InputLabel>

            <Box
                ref={editorRef}
                contentEditable={!disabled}
                disabled={disabled}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onInput={handleInput}
                onKeyDown={handleKeyDown}
                onClick={handleClickLink}
                onCompositionStart={() => (isComposing.current = true)}
                onCompositionEnd={() => (isComposing.current = false)}
                spellCheck={false}
                sx={{
                    position: "relative",
                    border:
                        isFocused || value
                            ? "none"
                            : `1px solid ${theme.palette.inputBorder.main}`,
                    borderRadius: "4px",
                    padding: "16.5px 14px",
                    minHeight: 150,
                    maxHeight: 150,
                    overflowY: "auto",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    "& a": {
                        color: theme.palette.primary.main,
                        pointerEvents: "auto",
                        textDecoration: "underline !important",
                        "&:hover": {
                            color: theme.palette.primary.main,
                            cursor: "pointer",
                            textDecoration: "underline !important"
                        }
                    },
                    ...(disabled && {
                        color: theme.palette.text.disabled,
                        cursor: "not-allowed",
                        pointerEvents: "none",
                        border: `1px solid ${theme.palette.grey[400]}`
                    })
                }}
            />
        </FormControl>
    );
};

export default LinkDetectionTextField;
